import { inject, Injectable, signal, WritableSignal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { EMPTY } from 'rxjs';
import { switchMap, map } from 'rxjs/operators';

import { PlanogramGroupsService } from '../../core/services/planogram-groups.service';
import { PlanogramsService } from '../../core/services/planograms.service';
import { SessionService } from '../../core/services/session.service';
import { SnackBarService } from '../../core/services/snack-bar.service';
import { Element } from '../../equipment/models/elements/element';
import { IPlanogram } from '../models/planogram';
import { IPlanogramData } from '../models/planogramData';
import { Product } from '../models/product';
import { NORMALIZED_ELEMENTS } from './planogram-activity.service';
import { PlanogramLayoutService } from './planogram-layout.service';
import { Utils } from '../../core/services/utils.service';

export interface PlanogramResolverGroup {
	id: number;
	name: string;
}

export interface PlanogramResolverState {
	loading: boolean;
	planograms: Array<IPlanogram>;
	group?: PlanogramResolverGroup;
}

@Injectable()
export class PlanogramResolverService {

	private readonly route: ActivatedRoute = inject(ActivatedRoute);
	private readonly router: Router = inject(Router);
	private readonly planogramsService: PlanogramsService = inject(PlanogramsService);
	private readonly planogramGroupsService: PlanogramGroupsService = inject(PlanogramGroupsService);
	private readonly sessionService: SessionService = inject(SessionService);
	private readonly snackBarService: SnackBarService = inject(SnackBarService);
	private readonly planogramLayoutService: PlanogramLayoutService = inject(PlanogramLayoutService);

	state$: WritableSignal<PlanogramResolverState> = signal<PlanogramResolverState>({ loading: true, planograms: [] });

	constructor() {
		void this.resolve();
	}

	private resolve(): void {
		const routeId: number = Number(this.route.snapshot.params['id']);
		const planId: number = Number(this.route.snapshot.queryParams['planogram']);

		if (Utils.isNullOrUndefined(routeId) || Utils.isNullOrUndefined(planId)) {
			void this.redirectToList();
			return;
		}

		if (this.isGroupRoute()) {
			this.resolveGroup(routeId, planId);
			return;
		}

		this.resolveStand(routeId, planId);
	}

	private isGroupRoute(): boolean {
		for (let route: ActivatedRoute | null = this.route; route; route = route.parent) {
			if (route.snapshot.url.some((segment) => segment.path === 'group')) {
				return true;
			}
		}

		return false;
	}

	private resolveGroup(groupId: number, planId: number): void {
		this.planogramGroupsService.getItem(groupId).pipe(
			switchMap((group) => {
				if (!group || group.plan_id !== planId || !group.planograms?.length) {
					void this.redirectToList('COMMON.PLANOGRAM.NOT_EXIST');
					return EMPTY;
				}

				return this.planogramsService.bulkFetch(group.planograms.map((mainStandId: number) => ({ planogram_id: mainStandId, plan_id: planId }))).pipe(
					map((planogramDataList: Array<IPlanogramData>) => ({
						planogramDataList,
						group: { id: group.id, name: group.name }
					}))
				);
			})
		).subscribe({
			next: ({ planogramDataList, group }: { planogramDataList: Array<IPlanogramData>; group: PlanogramResolverGroup }) => {
				const planograms: Array<IPlanogram> = [];

				for (const planogramData of planogramDataList) {
					if (!this.isValidPlanogramData(planogramData, planId)) return;

					planograms.push(this.buildPlanogram(planogramData, planId));
				}

				this.state$.set({ loading: false, planograms, group });
			},
			error: (error: HttpErrorResponse) => this.handleResolveError(error)
		});
	}

	private resolveStand(mainStandId: number, planId: number): void {
		this.planogramsService.getItem$$(mainStandId, { planogram_id: planId }).subscribe({
			next: (planogramData: IPlanogramData) => {
				if (!this.isValidPlanogramData(planogramData, planId)) return;

				this.state$.set({
					loading: false,
					planograms: [this.buildPlanogram(planogramData, planId)]
				});
			},
			error: (error: HttpErrorResponse) => this.handleResolveError(error)
		});
	}

	private isValidPlanogramData(planogramData: IPlanogramData, planId: number): boolean {
		if (!planogramData) {
			void this.redirectToList();
			return false;
		}

		if (planogramData.locked) {
			void this.redirectToList('COMMON.PLANOGRAM.LOCKED');
			return false;
		}

		if (!planogramData.planogram_stand?.map((p) => Number(p.planogram_id)).includes(planId)) {
			void this.redirectToList('COMMON.PLANOGRAM.NOT_EXIST');
			return false;
		}

		return true;
	}

	private buildPlanogram(planogramData: IPlanogramData, planId: number): IPlanogram {
		const products: Array<Product> = [];
		const planogram: IPlanogram = this.planogramLayoutService.getPlanogramObject(
			planogramData,
			products,
			planId,
			true
		);

		planogram.flip = false;

		(planogram.stand as any).orderedElements?.forEach((el: Element) => {
			if (NORMALIZED_ELEMENTS.includes(el.constructor.name)) el.normalize(planogram.stand);
		});

		return planogram;
	}

	private handleResolveError(error: HttpErrorResponse): void {
		if (error.status === 404) {
			void this.redirectToList('COMMON.PLANOGRAM.NOT_EXIST');
			return;
		}

		void this.redirectToList();
	}

	private redirectToList(messageText?: string): Promise<void> {
		return this.router.navigate(this.sessionService.getLink(['stand', 'list'])).then(() => {
            if (messageText) {
                this.snackBarService.setSnackBarNotification({ messageText });
            }
		});
	}
}
